import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { UntypedFormGroup, UntypedFormBuilder, Validators } from "@angular/forms";
import { GlobalValidators } from "./../../shared/validators/global-validators";
import { RegexPatterns } from "./../../shared/regex/regex-patterns";
import { PartialProfileService } from "./../shared/services/partial-profile.service";
import { NotificationsService } from "./../../shared/services/notifications.service";
import { ViewStateService } from "./../../shared/services/view-state.service";
import { CommonApiService } from "./../../shared/services/api/common-api.service";
import { AppSettingsService } from "./../../shared/services/app-settings.service";
import moment from "moment";
import { map, filter, takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";

@Component({
  selector: "app-partial-profile",
  templateUrl: "./partial-profile.component.html",
})
export class PartialProfileComponent implements OnInit, OnDestroy {
  private dateInputFormat = this.appSettings.IsCompatibilityMode
    ? "MM/DD/YYYY"
    : "YYYY-MM-DD";
  private destroy$ = new Subject<void>();

  studentForm: UntypedFormGroup;
  formLevelErrors = [];
  partialProfile: boolean = false;
  successfulSubmission: boolean = false;
  countries: any;
  studentId: string = null;
  badZipMessage = false;
  schoolCode: any;
  schoolName: any;
  alreadyComplete = false;

  mxd = new Date();
  mnd = new Date();
  minDate: Date = new Date(this.mnd.setFullYear(this.mnd.getFullYear() - 110));
  maxDate: Date = new Date(this.mxd.setFullYear(this.mxd.getFullYear() - 18));

  constructor(
    private formBuilder: UntypedFormBuilder,
    private commonApiService: CommonApiService,
    private viewState: ViewStateService,
    private router: Router,
    private partialProfileService: PartialProfileService,
    private notifications: NotificationsService,
    public appSettings: AppSettingsService
  ) {}

  ngOnInit() {
    this.viewState.startLoading();
    this.studentId = this.router.routerState.snapshot.root.queryParams["token"];
    this.buildForm();
    this.getStudentProfileData();
    this.retrieveCommonData();
    this.studentForm.get("countryId").patchValue(1);
  }
  get selectedCountry() {
    return this.studentForm.get("countryId").value;
  }
  get isUs() {
    return this.selectedCountry == 1;
  }
  onSubmit(updatedFormValues: any) {
    this.viewState.startLoading();
    this.validateForm();
    this.save();
  }

  getStudentProfileData() {
    this.viewState.startLoading();

    this.partialProfileService
      .getProfile(this.studentId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (resp) => {
          this.studentForm.patchValue(resp);
          this.populateSchoolInfo(resp);
          this.viewState.stopLoading();
        },
        (errorResponse) => {
          if (errorResponse.status === 400) {
            this.notifications.error(
              "Profile already completed",
              "This profile has already been completed or expired"
            );
            this.alreadyComplete = true;
          }
          // this.handleServerErrorResponse(errorResponse)
        }
      );
  }

  performZipCodeLookup() {
    this.badZipMessage = false;
    const zip = this.studentForm.value.postalCode
      ? this.studentForm.value.postalCode.split("-")
      : "";
    if (!zip) return;

    this.commonApiService
      .lookupStateAndCityByZipCode(zip[0])
      .pipe(
        takeUntil(this.destroy$),
        map((resp) => resp),
        filter((res) => res.length !== 0)
      )
      .subscribe(
        (val) => {
          const value = val[0] || "";
          if (!value) return;
          this.setFormValue(value);
        },
        (error) => {
          this.badZipMessage = true;
          console.log(error);
        }
      );
  }

  private populateSchoolInfo(formData: any) {
    this.schoolCode = formData.schoolID;
    this.schoolName = formData.schoolName;
  }

  private setFormValue(lookUpValues) {
    this.studentForm.patchValue({
      state: lookUpValues.stateCode,
      city: lookUpValues.city,
    });
  }

  private validateForm() {
    for (const control in this.studentForm.controls) {
      this.studentForm.controls[control].updateValueAndValidity();
    }

    let hasErrors = false;
    for (const field in this.formErrors) {
      this.formErrors[field] = "";
      const control = this.studentForm.get(field);
      if (control && control.invalid) {
        hasErrors = true;
        const messages = this.validationMessages[field];
        for (const key in control.errors) {
          this.formErrors[field] += messages[key] + " ";
        }
      }
    }
    if (hasErrors) {
      this.notifications.error(
        "Fields Invalid",
        "Please review the form and resubmit."
      );
      this.viewState.stopLoading();
    }
  }

  private buildForm(): void {
    let mnd = new Date();
    let mxd = new Date();
    this.minDate = new Date(mnd.setFullYear(mnd.getFullYear() - 110));
    this.maxDate = new Date(mxd.setFullYear(mxd.getFullYear() - 18));

    this.studentForm = this.formBuilder.group({
      id: [this.formValues.id],
      ssn: [
        this.formValues.ssn,
        [
          Validators.required,
          Validators.maxLength(9),
          Validators.pattern(RegexPatterns.ssn),
        ],
      ],
      firstName: [
        this.formValues.firstName,
        [
          Validators.required,
          Validators.maxLength(30),
          Validators.pattern(RegexPatterns.lettersOnly),
        ],
      ],
      middleName: [
        this.formValues.middleName,
        [
          Validators.maxLength(30),
          Validators.pattern(RegexPatterns.lettersOnly),
        ],
      ],
      lastName: [
        this.formValues.lastName,
        [
          Validators.required,
          Validators.maxLength(30),
          Validators.pattern(RegexPatterns.lettersOnly),
        ],
      ],
      suffix: [this.formValues.suffix],
      email: [
        { value: this.formValues.email, disabled: true },
        [Validators.required, Validators.maxLength(100), Validators.email],
      ],
      gender: [this.formValues.gender, Validators.required],
      countryId: [
        this.formValues.countryId,
        [Validators.required, Validators.maxLength(40)],
      ],
      address1: [
        this.formValues.address1,
        [
          Validators.required,
          Validators.maxLength(35),
          Validators.pattern(RegexPatterns.alphaNumericSpecial),
        ],
      ],
      address2: [
        this.formValues.address2,
        [
          Validators.maxLength(35),
          Validators.pattern(RegexPatterns.alphaNumericSpecial),
        ],
      ],
      postalCode: [
        this.formValues.postalCode,
        [
          Validators.required,
          Validators.maxLength(10),
          Validators.pattern(RegexPatterns.integersOnly),
        ],
      ],
      city: [
        this.formValues.city,
        [
          Validators.required,
          Validators.maxLength(35),
          Validators.pattern(RegexPatterns.lettersOnly),
        ],
      ],
      state: [
        this.formValues.state,
        [Validators.required, Validators.maxLength(50)],
      ],
      schoolID: [this.formValues.schoolID],
      homePhone: [
        this.formValues.homePhone,
        [
          Validators.maxLength(50),
          Validators.minLength(10),
          Validators.pattern(RegexPatterns.integersOnly),
        ],
      ],
      cellPhone: [
        this.formValues.cellPhone,
        [
          Validators.required,
          Validators.maxLength(50),
          Validators.minLength(10),
          Validators.pattern(RegexPatterns.integersOnly),
        ],
      ],
      graduationDate: [
        this.formValues.graduationDate,
        [
          GlobalValidators.gradDateMax,
          GlobalValidators.gradDateMin,
          GlobalValidators.validDate(this.dateInputFormat),
        ],
      ],
      birthday: [
        this.formValues.birthday,
        [
          GlobalValidators.validDate(this.dateInputFormat),
          GlobalValidators.minDate(this.minDate),
          GlobalValidators.maxDate(this.maxDate),
          Validators.required,
        ],
      ],
      degreeType: [this.formValues.degreeType],
      semesterHours: [
        this.formValues.semesterHours,
        Validators.pattern(RegexPatterns.integersOnlyGTZero),
      ],
    });
  }

  private save() {
    if (this.studentForm.valid) {
      this.partialProfileService
        .updateProfile(this.studentId, this.studentForm.getRawValue())
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (resp) => {
            this.viewState.stopLoading();
            this.notifications.success(
              "Success",
              "The student record was updated."
            );
            this.successfulSubmission = true;
          },
          (errorResponse) => this.handleServerErrorResponse(errorResponse)
        );
    }
  }

  private handleServerErrorResponse(response) {
    if (response.status === 500)
      this.notifications.error(
        "Unexpected Error",
        "An unexpected error occurred."
      );
    else this.mapServerErrorsToForm(response.error);

    this.viewState.stopLoading();
  }

  private retrieveCommonData() {
    this.commonApiService
      .getCountries()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res) => {
          this.countries = res;
          this.viewState.stopLoading();
        },
        (error) => {
          console.log(error);
        }
      );
  }

  private mapServerErrorsToForm(errors) {
    let unknownErrors = [];
    for (let error of errors) {
      this.formErrors[error.fieldName] = "";
      const control = this.studentForm.get(error.fieldName);
      this.formErrors[error.fieldName] += error.message + " ";
      if (!control) unknownErrors.push(error.message);
    }

    if (unknownErrors.length > 0)
      this.notifications.error(
        "Form Errors",
        "Please check the form for invalid values."
      );
    else
      this.notifications.error(
        "Unexpected Errors",
        "If these errors persist, please contact NBRC for assistance."
      );

    this.viewState.stopLoading();
  }

  private formValues = {
    id: this.studentId || null,
    ssn: "",
    firstName: "",
    middleName: "",
    lastName: "",
    suffix: "",
    email: "",
    gender: "",
    countryId: "",
    address1: "",
    address2: "",
    postalCode: "",
    city: "",
    state: "",
    schoolID: "",
    homePhone: "",
    cellPhone: "",
    graduationDate: "",
    degreeType: "",
    semesterHours: "",
    birthday: "",
  };

  formErrors = {
    ssn: "",
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    gender: "",
    countryId: "",
    address1: "",
    address2: "",
    postalCode: "",
    city: "",
    state: "",
    homePhone: "",
    cellPhone: "",
    graduationDate: "",
    degreeType: "",
    semesterHours: "",
    birthday: "",
  };

  validationMessages = {
    ssn: {
      required: "SSN is required.",
      maxlength: "SSN must be max 9 characters.",
      pattern: "SSN must be in the correct format.",
    },
    firstName: {
      required: "First Name is required.",
      maxlength: "First Name must be max 30 characters.",
      pattern: "First Name must be letters only.",
    },
    middleName: {
      maxlength: "Middle Name must be max 30 characters.",
      pattern: "Middle Name must be letters only.",
    },
    lastName: {
      required: "Last Name is required.",
      maxlength: "Last Name must be max 30 characters.",
      pattern: "Last Name must be letters only.",
    },
    email: {
      required: "Email is required.",
      maxlength: "Email must be max 100 characters.",
      email: "Email must be in the correct format.",
    },
    gender: {
      required: "Gender is required.",
    },
    countryId: {
      required: "Country is required.",
      maxlength: "Country must be max 40 characters.",
      pattern: "Country must be letters only.",
    },
    address1: {
      required: "Address is required.",
      maxlength: "Address must be max 35 characters.",
      pattern: "Address must be letters & numbers.",
    },
    address2: {
      maxlength: "Address must be max 35 characters.",
      pattern: "Address must be letters & numbers.",
    },
    postalCode: {
      required: "Zip / Postal Code is required.",
      maxlength: "Zip / Postal Code must be max 10 characters.",
      pattern: "Zip / Postal Code must be numbers only.",
    },
    city: {
      required: "City is required.",
      maxlength: "City must be max 35 characters.",
      pattern: "City must be letters only.",
    },
    state: {
      required: "State is required.",
      maxlength: "State must be max 50 characters.",
    },
    homePhone: {
      required: "Home Phone is required.",
      maxlength: "Home Phone must be max 10 characters.",
      minlength: "Home Phone must be minimum 10 characters.",
      pattern: "Home Phone must be numbers only.",
    },
    cellPhone: {
      required: "Cell Phone is required.",
      maxlength: "Cell Phone must be max 10 characters.",
      minlength: "Cell Phone must be minimum 10 characters.",
      pattern: "Cell Phone must be numbers only.",
    },
    graduationDate: {
      gradDateMax: "Graduation date must be today or in the past.",
      gradDateMin: "Graduation date must be greater than Jan 01, 2000.",
      validDate: "Graduation date must be a valid date.",
    },
    degreeType: {
      requiredIf: "Degree Type is required.",
    },
    semesterHours: {
      requiredIf: "Semester Hours are required.",
      pattern: "Negative hours and 0 are not allowed.",
    },
    birthday: {
      required: "Date of birth required.",
      validDate: "Date of birth must be a valid date.",
      maxDate:
        "Date of birth must be at least 18 years ago and not in the future.",
      minDate: "Date of birth must be greater than {minDate}.".replace(
        "{minDate}",
        moment(this.minDate).format("MMM DD, YYYY")
      ),
    },
  };

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
